import type { CommunityType } from '~/entities/container/community/interfaces/community'
import type { MEDIA_PROCESSING_STATUS } from '~/entities/content/media/composables/media-format'
import type { IMedia } from '~/entities/content/media/interfaces/media'
import type { IMediaType } from '~/entities/content/media/interfaces/media-type'
import type { IUser } from '~/entities/user/user/interfaces/user'

import { IContentModel } from '~/features-shared/content/interfaces/content-model'
import type {
  IDerivedTypeData,
  IDerivedTypeMedia,
} from '~/features-shared/content-derived-type/interfaces/derived-types'
import type { IFacetSetting } from '~/features-shared/facet/interfaces/facet'
import type { Acl } from '~/features-shared/libs/interfaces/authorization'
import type { IEntity, SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'
import type { QuestionMaturity, WedoMaturity } from '~/features-shared/maturities/interfaces/maturities'

/**
 * Content type and i3dx domain name mapping.
 */
export enum I3dxDomain {
  POST = 'Post',
  MEDIA = 'RichMedia',
  QUESTION = 'Question',
  WIKI = 'WikitreePage',
  IDEA = 'Idea',
  COMMUNITY = 'Community',
}

/**
 * Defines media types that can be created with Media recorder
 */
export enum MediaModel {
  Voice = 'voice', // Exclusive to conversation
  Video = 'video', // Exclusive to conversation
}

export enum IContentModelExalead {
  Post = 'Post',
  Idea = 'Idea',
  Media = 'Media',
  Conference = 'Conference',
  Survey = 'Survey',
  Wiki = 'WikitreePage',
  Iquestion = 'Question',
  Wedo = 'WeDo',
}
// tactical fix
export const CommunityContentModel = [
  IContentModel.Post,
  IContentModel.Idea,
  IContentModel.Question,
  IContentModel.Media,
  IContentModel.Conference,
  IContentModel.Survey,
  IContentModel.Wedo,
  IContentModel.Wiki,
  IContentModel.Streaming,
]

export const ContentModelExalead = [
  IContentModelExalead,
]

export enum Event {
  Published = 'Created',
  Commented = 'Commented',
  Answered = 'Answered',
  Revised = 'Updated',
  Edited = 'Edited',
  Ended = 'Ended',
  Started = 'Started',
  StatusChanged = 'Status Changed',
  Validated = 'Answer Validated',
}

export enum PublicationState {
  Draft = 'DRAFT',
  Published = 'PUBLISHED',
  Deleted = 'DELETED',
}

export interface IContent extends IEntity {
  title: string

  /**
   * Full content of the Content.
   * @info May contains HTML tags.
   */
  description: string

  community_id: string

  /** Thread subject URI (community or conversation subject URI). */
  community_subject_uri: SUBJECT_6W_URI

  community_type: CommunityType
  community_title: string
  conversation_title?: string
  derived_type?: IDerivedTypeData

  /**
   * Content's summary generated by the backend.
   * @info Contains no HTML tags.
   */
  summary?: string // s55: TODO: I set it optional as I don't know if this attribute is present for all content.

  /**
   * Content's rich summary generated by the backend.
   * @info Contains HTML tags.
   */
  enhanced_summary: string

  /**
   * Should show the see more button in summary views.
   * @info If true, it means that the `summary` & the `enhanced_summary` are truncated
   *       compared to the original content.
   */
  seeMore?: boolean

  /**
   * Content model type.
   * @interface {@link IContentModel}
   */
  model: IContentModel

  stats_viewed: number
  moderation_status: 'no_moderation' | 'updated_by_author' | 'updated' | 'deleted' | 'delete_by_author'
  preview_fingerprint?: string
  preview_media_type?: IMediaType
  preview_original_width?: number // DEPRECATED -> preview.???
  preview_original_height?: number // DEPRECATED -> preview.???
  preview_media_processing_version?: 0 | 1 // DEPRECATED -> preview.???
  preview_media_processing_status?: MEDIA_PROCESSING_STATUS // DEPRECATED -> preview.???
  preview_url?: string
  thumbnail_id: string
  thumbnails_infos?: IMedia[]

  /**
   * Published date.
   * @info Will not be updated when the content will be updated.
   * @example "2023-08-30T14:13:27.000Z"
   */
  published_at: string

  moderated_at: string

  /**
   * Content's author (user who creates initialy this content).
   * @interface {@link IUser}
   */
  author: IUser

  last_contributed_at?: string
  last_contributed_event?: Event
  last_contributed_author?: IUser
  forwarded_from?: IUser
  maturity?: QuestionMaturity | WedoMaturity
  published?: boolean
  publication_state?: PublicationState
  last_publisher?: IUser // Set when the content has been updated while the user is editing this content
  last_publisher_hashkey?: string

  tags6w?: any[]
  tags?: any[]
  tags6w_implicit?: any[]
  tags6w_subjecturi?: string // Only for wedos
  /** Thumbnail subject uri */
  thumbnail_subject6wuri?: string

  /**
   * Current user's access rights on this content.
   * @interface {@link Acl}
   */
  _acl?: Acl

  preview?: Pick<IMedia, 'width' | 'height' | 'preview_fingerprint' | 'media_file_ext' | 'subject_uri'
  | 'processing_version' | 'status' | 'type' >

  // conference & ritual specific
  from_ritual?: boolean
  media?: IDerivedTypeMedia[]
  height?: number
  width?: number
  status?: MEDIA_PROCESSING_STATUS
  type?: IMediaType
  processing_version?: number
  /**
   * This is set to true only when the content is a part of community for which TOU has not been signed yet by user
   */
  tou_to_sign?: boolean
}

export type ContentTemplate = Omit<IContent, 'type'> & {
  /** Content template model */
  type: string
}

export interface IContentTypeSetting extends IFacetSetting {
  model: IContentModel
  allowContributorsToCreate: boolean
  disableAllowContributorsToCreateEdition: boolean
}

export interface CreateContentCmpData {
  message?: string
  title?: string
  questions?: {
    description: string
  }[]
  topics?: {
    description: string
  }[]
  /** is modified */
  isModified?: boolean
}

export interface CreateContentCmpExpose {
  /** Check if the edition can be canceled */
  canCancel: () => Promise<boolean>
  /** Retrieve the editor content */
  getData: (draft?: boolean) => Promise<CreateContentCmpData>
  /**
   * Validate the creation content form.
   * @info Should returns TRUE if the validation is successful.
   * @info Should throw an error (with the error message) if the validation is falsy.
   */
  validate?: () => Promise<boolean>
}

export interface EditContentCmpData {
  message?: string
  description?: string
  title?: string
  isModified?: boolean

  // #region Wedo
  maturity?: WedoMaturity
  assignees_to_add?: string[]
  assignees_to_remove?: string[]
  attachments_to_add?: IMedia[]
  attachments_to_remove?: { subject_uri: SUBJECT_6W_URI }[]
  end_date?: string
  // #endregion Wedo

  // #region Survey
  startDate?: string
  startHour?: string
  endDate?: string
  endHour?: string
  // #endregion Survey
}

/** EditContentCmpExpose */
export interface EditContentCmpExpose {
  /** Check if the edition can be canceled */
  canCancel?: () => Promise<boolean>
  /** Retrieve the editor content */
  getData: () => Promise<EditContentCmpData>
}

/** Custom error message used as error code for content. */
export enum ContentErrorMessage {
  CanceledByUser = 'CANCELED_BY_USER',
  MediaKo = 'MEDIA_KO',
}
/** recycle bin content */
export interface RecycleBinContent extends IContent {
  /** Author of content */
  author:	IUser
  /** Body */
  body?: string
  /** Summary of the content */
  summary?: string
  /** Date on which content created */
  creation_date: Date
  /** Date on which content deleted */
  deletion_date: Date
  /** Community uri of content */
  community_uri: SUBJECT_6W_URI
  /** Person who deleted the content */
  deleted_by: IUser
}
